import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Helmet } from 'react-helmet-async';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle, Crown, TrendingUp, BarChart3, Loader2, Receipt, Mail } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { usePlanLimits } from '@/hooks/usePlanLimits';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { PaymentReceipt } from '@/components/PaymentReceipt';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';

const Billing = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [payments, setPayments] = useState<any[]>([]);
  const [selectedReceipt, setSelectedReceipt] = useState<any>(null);
  const [isYearly, setIsYearly] = useState(false);
  const planLimits = usePlanLimits();

  const plans = [
    {
      name: 'Free',
      monthlyPrice: 0,
      yearlyPrice: 0,
      period: 'forever',
      description: 'Perfect for getting started',
      features: [
        'Up to 2 forms',
        '50 submissions/month',
        'Basic analytics',
        'Multi-channel delivery',
        'Email support',
      ],
      current: user?.plan === 'free',
      popular: false,
    },
    {
      name: 'Starter',
      monthlyPrice: 2500,
      yearlyPrice: 25000,
      period: isYearly ? 'per year' : 'per month',
      description: 'For growing businesses',
      features: [
        'Up to 10 forms',
        '500 submissions/month',
        'Advanced analytics',
        'Custom branding',
        'Email notifications',
        'Priority support',
        'Export data (CSV)',
      ],
      current: user?.plan === 'starter',
      popular: true,
      savings: '₦5,000 saved yearly',
    },
    {
      name: 'Pro',
      monthlyPrice: 5000,
      yearlyPrice: 50000,
      period: isYearly ? 'per year' : 'per month',
      description: 'For established businesses',
      features: [
        'Unlimited forms',
        'Unlimited submissions',
        'Advanced CRM features',
        'API access',
        'White-label solution',
        '24/7 dedicated support',
        'Advanced integrations',
      ],
      current: user?.plan === 'pro',
      popular: false,
      savings: '₦10,000 saved yearly',
    },
    {
      name: 'Enterprise',
      monthlyPrice: null,
      yearlyPrice: null,
      period: 'custom',
      description: 'For large organizations',
      features: [
        'Everything in Pro',
        'Custom form limits',
        'Dedicated account manager',
        'Custom integrations',
        'SLA guarantees',
        'Priority feature requests',
        'Onboarding & training',
      ],
      current: false,
      popular: false,
      isEnterprise: true,
    },
  ];

  // Handle payment callback
  useEffect(() => {
    const reference = searchParams.get('reference');
    const trxref = searchParams.get('trxref');
    const canceled = searchParams.get('canceled');
    
    // If user explicitly canceled
    if (canceled === 'true') {
      handleCanceledPayment();
      return;
    }
    
    // If we have a reference, verify it
    if (reference || trxref) {
      verifyPayment(reference || trxref || '');
    }
  }, [searchParams]);

  useEffect(() => {
    if (user) {
      fetchPayments();
    }
  }, [user]);

  const handleCanceledPayment = async () => {
    toast({
      title: 'Payment Canceled',
      description: 'You have canceled the payment process.',
      variant: 'default',
    });
    
    // Clear URL parameters
    setSearchParams({});
    
    // Refresh payment history to show updated status
    if (user) {
      await fetchPayments();
    }
  };

  const verifyPayment = async (reference: string) => {
    if (!reference || isVerifying) return;
    
    setIsVerifying(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('paystack-verify', {
        body: { reference },
      });

      if (error) throw error;

      if (data?.verified) {
        toast({
          title: 'Payment Successful!',
          description: 'Your plan has been upgraded successfully.',
          duration: 5000,
        });
        
        // Clear URL parameters
        setSearchParams({});
        
        // Refresh the page to show updated plan
        window.location.reload();
      } else {
        // Payment failed or was canceled
        toast({
          title: 'Payment Not Completed',
          description: 'Your payment was not completed. Please try again or contact support.',
          variant: 'destructive',
        });
        
        // Clear URL parameters
        setSearchParams({});
        
        // Refresh payment history
        await fetchPayments();
      }
    } catch (error: any) {
      console.error('Payment verification error:', error);
      toast({
        title: 'Payment Failed',
        description: error.message || 'Failed to verify payment. The transaction was not completed.',
        variant: 'destructive',
      });
      
      // Clear URL parameters
      setSearchParams({});
      
      // Refresh payment history
      await fetchPayments();
    } finally {
      setIsVerifying(false);
    }
  };

  const fetchPayments = async () => {
    try {
      const { data, error } = await supabase
        .from('payments')
        .select('*')
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false })
        .limit(5);

      if (error) throw error;
      setPayments(data || []);
    } catch (error: any) {
      console.error('Error fetching payments:', error);
    }
  };

  const handleUpgrade = async (plan: typeof plans[0]) => {
    if (plan.current || (plan.monthlyPrice === 0 && plan.yearlyPrice === 0)) return;

    // Handle enterprise plan
    if (plan.isEnterprise) {
      window.location.href = `mailto:bravaxforms@gmail.com?subject=Enterprise Plan Inquiry&body=Hi, I'm interested in the Enterprise plan for Bravax Forms.`;
      return;
    }

    setIsLoading(true);

    try {
      const amount = isYearly ? plan.yearlyPrice : plan.monthlyPrice;
      const { data, error } = await supabase.functions.invoke('paystack-initialize', {
        body: {
          email: user?.email,
          amount: amount,
          plan: plan.name,
          metadata: {
            user_name: user?.name,
            current_plan: user?.plan,
            billing_period: isYearly ? 'yearly' : 'monthly',
          },
        },
      });

      if (error) throw error;

      if (data?.authorization_url) {
        // Redirect to Paystack payment page
        window.location.href = data.authorization_url;
      } else {
        throw new Error('No authorization URL received from payment provider');
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to initialize payment',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const currentPlan = plans.find((plan) => plan.current) || plans[0];

  const getUsagePercentage = (used: number, limit: number) => {
    if (limit === 0) return 0;
    return Math.min((used / limit) * 100, 100);
  };

  if (isVerifying) {
    return (
      <DashboardLayout>
        <div className="p-6 flex items-center justify-center min-h-[400px]">
          <Card className="card-elegant max-w-md w-full">
            <CardContent className="p-8 text-center">
              <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
              <h2 className="text-xl font-bold text-foreground mb-2">Verifying Payment</h2>
              <p className="text-muted-foreground">
                Please wait while we confirm your payment with Paystack...
              </p>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Helmet>
        <title>Billing | Bravax Forms</title>
      </Helmet>
      <div className="p-4 sm:p-6 lg:p-8 space-y-8 animate-fade-in">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Billing & Plans</h1>
          <p className="text-muted-foreground">Manage your subscription and billing information</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="card-elegant">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center space-x-2">
                    <Crown className="h-5 w-5 text-primary" />
                    <span>Current Plan</span>
                  </CardTitle>
                  <CardDescription>Your active subscription</CardDescription>
                </div>
                {currentPlan.name !== 'Free' && <Badge variant="default">Active</Badge>}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="text-2xl font-bold text-foreground">{currentPlan.name}</h3>
                <div className="flex items-baseline space-x-1">
                  <span className="text-3xl font-bold text-primary">
                    ₦{(isYearly ? currentPlan.yearlyPrice : currentPlan.monthlyPrice)?.toLocaleString() || 0}
                  </span>
                  <span className="text-muted-foreground">/{currentPlan.period}</span>
                </div>
                <p className="text-muted-foreground mt-2">{currentPlan.description}</p>
              </div>
            </CardContent>
          </Card>

          <Card className="card-elegant">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <BarChart3 className="h-5 w-5 text-primary" />
                <span>Usage</span>
              </CardTitle>
              <CardDescription>Your current usage this month</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {planLimits.isLoading ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              ) : (
                <>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">Forms Created</span>
                      <span className="text-sm text-muted-foreground">
                        {planLimits.formsCount} / {planLimits.formsLimit}
                      </span>
                    </div>
                    <Progress value={getUsagePercentage(planLimits.formsCount, planLimits.formsLimit)} className="h-2" />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">Submissions</span>
                      <span className="text-sm text-muted-foreground">
                        {planLimits.submissionsCount} / {planLimits.submissionsLimit}
                      </span>
                    </div>
                    <Progress value={getUsagePercentage(planLimits.submissionsCount, planLimits.submissionsLimit)} className="h-2" />
                  </div>

                  {getUsagePercentage(planLimits.submissionsCount, planLimits.submissionsLimit) > 80 && (
                    <Alert>
                      <TrendingUp className="h-4 w-4" />
                      <AlertDescription>
                        You're approaching your submission limit. Consider upgrading your plan.
                      </AlertDescription>
                    </Alert>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>

        <div>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-foreground">Available Plans</h2>
            <div className="flex items-center gap-3">
              <Label htmlFor="billing-toggle" className="text-sm text-muted-foreground">Monthly</Label>
              <Switch 
                id="billing-toggle"
                checked={isYearly} 
                onCheckedChange={setIsYearly}
              />
              <Label htmlFor="billing-toggle" className="text-sm font-medium text-foreground">
                Yearly <Badge variant="secondary" className="ml-1">Save up to 17%</Badge>
              </Label>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {plans.map((plan, index) => (
              <Card
                key={index}
                className={cn(
                  'card-elegant relative',
                  plan.popular && 'ring-2 ring-primary shadow-primary',
                  plan.current && 'bg-muted/30'
                )}
              >
                {plan.popular && (
                  <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-primary text-primary-foreground">
                    Most Popular
                  </Badge>
                )}
                {plan.current && (
                  <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-success text-success-foreground">
                    Current Plan
                  </Badge>
                )}
                <CardHeader className="text-center">
                  <CardTitle className="text-xl">{plan.name}</CardTitle>
                  <div className="py-4">
                    {plan.isEnterprise ? (
                      <>
                        <span className="text-3xl font-bold text-foreground">Custom</span>
                        <span className="text-muted-foreground block text-sm mt-1">Contact us</span>
                      </>
                    ) : (
                      <>
                        <span className="text-3xl font-bold text-foreground">
                          ₦{(isYearly ? plan.yearlyPrice : plan.monthlyPrice)?.toLocaleString() || 0}
                        </span>
                        <span className="text-muted-foreground">/{plan.period}</span>
                        {isYearly && plan.savings && (
                          <Badge variant="secondary" className="mt-2 block text-xs">
                            {plan.savings}
                          </Badge>
                        )}
                      </>
                    )}
                  </div>
                  <CardDescription>{plan.description}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <ul className="space-y-3">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start">
                        <CheckCircle className="h-4 w-4 text-primary mr-3 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    className={cn(
                      'w-full mt-6', 
                      plan.current ? 'opacity-50 cursor-not-allowed' : 
                      plan.isEnterprise ? 'btn-accent' :
                      plan.popular ? 'btn-hero' : 'btn-accent'
                    )}
                    disabled={plan.current || (isLoading && !plan.isEnterprise) || (plan.monthlyPrice === 0 && plan.yearlyPrice === 0)}
                    onClick={() => handleUpgrade(plan)}
                  >
                    {isLoading && !plan.isEnterprise ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    {plan.isEnterprise ? (
                      <>
                        <Mail className="h-4 w-4 mr-2" />
                        Contact Sales
                      </>
                    ) : plan.current ? (
                      'Current Plan'
                    ) : plan.monthlyPrice === 0 && plan.yearlyPrice === 0 ? (
                      'Free Forever'
                    ) : (
                      `Upgrade to ${plan.name}`
                    )}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {payments.length > 0 && (
          <Card className="card-elegant">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Receipt className="h-5 w-5 text-primary" />
                Payment History & Receipts
              </CardTitle>
              <CardDescription>View your transactions and download receipts</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {payments.map((payment) => (
                  <div key={payment.id} className="flex items-center justify-between p-4 border border-border rounded-lg hover:border-primary/50 transition-colors">
                    <div className="flex-1">
                      <h4 className="font-medium text-foreground">{payment.plan} Plan</h4>
                      <p className="text-sm text-muted-foreground">
                        {new Date(payment.created_at).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="font-bold text-foreground">
                          {payment.currency} {Number(payment.amount).toLocaleString()}
                        </div>
                        <Badge 
                          variant={
                            payment.status === 'success' || payment.status === 'completed' 
                              ? 'default' 
                              : payment.status === 'failed' || payment.status === 'canceled'
                              ? 'destructive'
                              : 'secondary'
                          }
                        >
                          {payment.status}
                        </Badge>
                      </div>
                      {(payment.status === 'success' || payment.status === 'completed') && (
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => setSelectedReceipt(payment)}
                            >
                              <Receipt className="h-4 w-4 mr-2" />
                              View Receipt
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-2xl">
                            <DialogHeader>
                              <DialogTitle>Payment Receipt</DialogTitle>
                            </DialogHeader>
                            {selectedReceipt && (
                              <PaymentReceipt 
                                payment={selectedReceipt}
                                userEmail={user?.email}
                                userName={user?.name}
                              />
                            )}
                          </DialogContent>
                        </Dialog>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Billing;
